import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMagnifyingGlass,
  faXmark,
  faChevronDown,
  faChevronRight,
  faPlus,
  faMinus,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import {
  fetchCpvTree,
  type CpvFlat,
} from "../../api/codCpvTreeApi";
import {
  buildTree,
  branchCodes,
  deriveSelection,
  searchTree,
  type BuiltTree,
  type CpvNode,
  type SearchResult,
  type SelectionDerived,
} from "./treeBuilder";
import "../../css/treeSelectCpv.css";

export interface TreeSelectCpvProps {
  value: string;
  onChange: (csv: string) => void;
  variant?: "include" | "exclude";
  label?: string;
}

const parseValue = (csv: string): string[] =>
  csv
    ? csv
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
    : [];

const stringifyValue = (set: Set<string>): string =>
  Array.from(set).sort().join(",");

const TreeSelectCpv: React.FC<TreeSelectCpvProps> = ({
  value,
  onChange,
  variant = "include",
  label,
}) => {
  const [tree, setTree] = useState<BuiltTree | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const selected = useMemo(() => new Set(parseValue(value)), [value]);
  const valueRef = useRef(value);
  valueRef.current = value;

  const derived: SelectionDerived = useMemo(
    () => (tree ? deriveSelection(tree, selected) : { state: new Map() }),
    [tree, selected]
  );

  // Carga del árbol (cacheado globalmente)
  useEffect(() => {
    if (!open || tree || loading) return;
    setLoading(true);
    setError(null);
    fetchCpvTree()
      .then((flat: CpvFlat[]) => setTree(buildTree(flat)))
      .catch((err) => setError(err.message || "Error cargando CPVs"))
      .finally(() => setLoading(false));
  }, [open, tree, loading]);

  // Debounce de la búsqueda
  const isTyping = search !== debouncedSearch;
  useEffect(() => {
    const id = window.setTimeout(() => setDebouncedSearch(search), 250);
    return () => window.clearTimeout(id);
  }, [search]);

  const searchResult: SearchResult | null = useMemo(() => {
    if (!tree || !debouncedSearch.trim()) return null;
    return searchTree(tree, debouncedSearch);
  }, [tree, debouncedSearch]);

  const isExpanded = useCallback(
    (code: string): boolean => {
      if (searchResult) return searchResult.ancestorsToExpand.has(code);
      return expanded.has(code);
    },
    [expanded, searchResult]
  );

  const toggleExpand = useCallback(
    (code: string) => {
      if (searchResult) return; // durante búsqueda no se toggle manual
      setExpanded((prev) => {
        const next = new Set(prev);
        if (next.has(code)) next.delete(code);
        else next.add(code);
        return next;
      });
    },
    [searchResult]
  );

  const emitChange = useCallback(
    (next: Set<string>) => {
      const csv = stringifyValue(next);
      if (csv !== valueRef.current) onChange(csv);
    },
    [onChange]
  );

  const handleToggleNode = useCallback(
    (node: CpvNode) => {
      const branch = branchCodes(node);
      const next = new Set(selected);
      // Si toda la rama ya está seleccionada → desmarcar la rama completa.
      // Si no → marcar padre + todos los descendientes como códigos individuales.
      const fullySelected = branch.every((c) => next.has(c));
      if (fullySelected) {
        for (const c of branch) next.delete(c);
      } else {
        for (const c of branch) next.add(c);
      }
      emitChange(next);
    },
    [selected, emitChange]
  );

  const handleRemoveChip = useCallback(
    (code: string) => {
      const next = new Set(selected);
      next.delete(code);
      emitChange(next);
    },
    [selected, emitChange]
  );

  const visibleNodes = useMemo(() => {
    if (!tree) return [] as CpvNode[];
    if (!searchResult) return tree.roots;
    // Durante búsqueda mostramos solo raíces que tengan algún descendiente match
    // (los ancestors están en ancestorsToExpand).
    return tree.roots.filter(
      (r) =>
        searchResult.matchSet.has(r.code) ||
        searchResult.ancestorsToExpand.has(r.code)
    );
  }, [tree, searchResult]);

  const selectedDetails = useMemo(() => {
    if (!tree)
      return [] as {
        code: string;
        label: string;
        clasificacion: string | null;
      }[];
    return Array.from(selected)
      .sort()
      .map((code) => {
        const node = tree.byCode.get(code);
        return {
          code,
          label: node?.label || "(no encontrado en diccionario)",
          clasificacion: node?.clasificacion ?? null,
        };
      });
  }, [tree, selected]);

  return (
    <div
      className={`cpvtree ${variant === "exclude" ? "cpvtree--exclude" : ""} ${
        open ? "" : "cpvtree--collapsed"
      }`}
    >
      <div
        className="cpvtree-header"
        onClick={() => setOpen((v) => !v)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setOpen((v) => !v);
          }
        }}
      >
        <span className="cpvtree-header-summary">
          {label || (variant === "exclude" ? "CPVs a ignorar" : "CPVs")}
          <span className="cpvtree-badge">{selected.size}</span>
        </span>
        <span className="cpvtree-header-toggle">
          <FontAwesomeIcon icon={open ? faChevronDown : faChevronRight} />{" "}
          {open ? "Contraer" : "Expandir"}
        </span>
      </div>

      {open && (
        <div className="cpvtree-body">
          <div className="cpvtree-search">
            <FontAwesomeIcon
              icon={faMagnifyingGlass}
              className="cpvtree-search-icon"
            />
            <input
              type="text"
              className="cpvtree-search-input"
              placeholder="Buscar por código o descripción…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {isTyping && (
              <FontAwesomeIcon
                icon={faSpinner}
                spin
                className="cpvtree-search-spinner"
              />
            )}
            {search && !isTyping && (
              <button
                type="button"
                className="cpvtree-search-clear"
                onClick={() => setSearch("")}
                aria-label="Limpiar búsqueda"
              >
                <FontAwesomeIcon icon={faXmark} />
              </button>
            )}
          </div>

          <div className="cpvtree-treebox">
            {loading && <div className="cpvtree-loading">Cargando CPVs…</div>}
            {error && <div className="cpvtree-error">{error}</div>}
            {!loading && !error && tree && visibleNodes.length === 0 && (
              <div className="cpvtree-empty">
                {searchResult
                  ? "No hay coincidencias"
                  : "No hay CPVs"}
              </div>
            )}
            {!loading &&
              !error &&
              tree &&
              visibleNodes.map((node) => (
                <TreeNode
                  key={node.code}
                  node={node}
                  derived={derived}
                  isExpanded={isExpanded}
                  toggleExpand={toggleExpand}
                  handleToggleNode={handleToggleNode}
                  searchResult={searchResult}
                />
              ))}
          </div>

          <div className="cpvtree-selected">
            <div className="cpvtree-selected-title">
              Seleccionados ({selected.size})
            </div>
            <div className="cpvtree-chips">
              {selectedDetails.length === 0 && (
                <span className="cpvtree-empty-chips">
                  Sin selección — todos los CPVs aplicarán.
                </span>
              )}
              {selectedDetails.map((s) => (
                <span key={s.code} className="cpvtree-chip" title={s.label}>
                  <span className="cpvtree-chip-code">{s.code}</span>
                  {s.clasificacion && (
                    <span className="cpvtree-chip-clasif">
                      {s.clasificacion}
                    </span>
                  )}
                  <span className="cpvtree-chip-label">{s.label}</span>
                  <button
                    type="button"
                    className="cpvtree-chip-remove"
                    onClick={() => handleRemoveChip(s.code)}
                    aria-label={`Quitar ${s.code}`}
                  >
                    <FontAwesomeIcon icon={faXmark} />
                  </button>
                </span>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

interface TreeNodeProps {
  node: CpvNode;
  derived: SelectionDerived;
  isExpanded: (code: string) => boolean;
  toggleExpand: (code: string) => void;
  handleToggleNode: (node: CpvNode) => void;
  searchResult: SearchResult | null;
}

const TreeNodeImpl: React.FC<TreeNodeProps> = ({
  node,
  derived,
  isExpanded,
  toggleExpand,
  handleToggleNode,
  searchResult,
}) => {
  const hasChildren = node.children.length > 0;
  const expanded = isExpanded(node.code);

  const nodeState = derived.state.get(node.code);
  const isChecked = nodeState === "checked";
  const isIndeterminate = nodeState === "partial";

  const checkboxRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    if (checkboxRef.current) {
      checkboxRef.current.indeterminate = isIndeterminate;
    }
  }, [isIndeterminate]);

  const isMatch = searchResult?.matchSet.has(node.code) ?? false;

  // Durante búsqueda solo renderizamos hijos que son match o ancestros de match
  const childrenToRender = searchResult
    ? node.children.filter(
        (c) =>
          searchResult.matchSet.has(c.code) ||
          searchResult.ancestorsToExpand.has(c.code)
      )
    : node.children;

  return (
    <>
      <div className="cpvtree-node">
        {hasChildren ? (
          <button
            type="button"
            className="cpvtree-toggle"
            onClick={() => toggleExpand(node.code)}
            aria-label={expanded ? "Contraer" : "Expandir"}
          >
            <FontAwesomeIcon icon={expanded ? faMinus : faPlus} />
          </button>
        ) : (
          <span className="cpvtree-toggle cpvtree-toggle--placeholder">·</span>
        )}
        <input
          ref={checkboxRef}
          type="checkbox"
          className="cpvtree-checkbox"
          checked={isChecked}
          onChange={() => handleToggleNode(node)}
        />
        <span className="cpvtree-code">{node.code}</span>
        {node.clasificacion && (
          <span className="cpvtree-clasif">{node.clasificacion}</span>
        )}
        <span
          className={`cpvtree-label ${isMatch ? "cpvtree-label--match" : ""}`}
        >
          {node.label}
        </span>
      </div>
      {hasChildren && expanded && childrenToRender.length > 0 && (
        <div className="cpvtree-children">
          {childrenToRender.map((child) => (
            <TreeNode
              key={child.code}
              node={child}
              derived={derived}
              isExpanded={isExpanded}
              toggleExpand={toggleExpand}
              handleToggleNode={handleToggleNode}
              searchResult={searchResult}
            />
          ))}
        </div>
      )}
    </>
  );
};

const TreeNode = React.memo(TreeNodeImpl, (prev, next) => {
  return (
    prev.node === next.node &&
    prev.derived === next.derived &&
    prev.searchResult === next.searchResult &&
    prev.isExpanded === next.isExpanded &&
    prev.toggleExpand === next.toggleExpand &&
    prev.handleToggleNode === next.handleToggleNode
  );
});

export default TreeSelectCpv;
